import { GET_USER_PROFILE, GetUserProfileData, GetUserProfileVars, UserProfile } from "common/graphql";
import { useAuth0 } from "@auth0/auth0-react";
import { ACCOUNT_ID_CLAIM, HASURA_CLAIMS_NAMESPACE, USER_ID_CLAIM, UserClaims } from "common/models/UserClaims";
import { ApolloClient, ApolloError, NetworkStatus, QueryHookOptions, QueryResult, useQuery } from "@apollo/client";
import { useContext, useMemo } from "react";
import AccountContext, { DEFAULT_ACCOUNT_CONTEXT } from "../context/AccountContext/AccountContext";

/**
 * Returns the authenticated user's profile, with `userProfile.account` resolved
 * to the user's active operational context.
 *
 * When an `AccountContextProvider` has a selected context, the query fetches
 * that account via `account_by_pk` and the hook overwrites
 * `userProfile.account` with it. Existing consumers continue reading
 * `userProfile.account` and automatically see the currently-selected context
 * — no changes needed at callsites.
 *
 * In apps without an `AccountContextProvider` (e.g. curbpass), no
 * `activeAccountId` is sent, `@include` skips the extra field, and
 * `userProfile.account` passes through unchanged from the JWT-linked row.
 */
export function useUserProfile(options?: UseUserProfileOptions): UseUserProfileResult {
  const { user: userClaims } = useAuth0<UserClaims>();
  const hasuraClaims = userClaims?.[HASURA_CLAIMS_NAMESPACE];
  const userId = hasuraClaims?.[USER_ID_CLAIM] || "";
  const jwtAccountId = hasuraClaims?.[ACCOUNT_ID_CLAIM] || "";

  // Read AccountContext directly rather than via useAccountContext(), which throws
  // outside a provider. Callers in curbpass (no provider) should still work — they
  // get contextId = null and skip the activeAccount fetch entirely.
  const accountContext = useContext(AccountContext);
  const selectedContextId = accountContext === DEFAULT_ACCOUNT_CONTEXT ? null : accountContext.contextId;
  // Only fetch `activeAccount` when the selected context actually differs from
  // the JWT account — otherwise `userProfile.account` from `userProfileFragment`
  // already is the right row and the extra round-trip is wasted.
  const hasActiveAccountId = Boolean(selectedContextId) && selectedContextId !== jwtAccountId;

  const { data, previousData, ...rest } = useQuery<GetUserProfileData, GetUserProfileVars>(GET_USER_PROFILE, {
    ...options,
    variables: {
      userId,
      activeAccountId: selectedContextId ?? "",
      hasActiveAccountId,
    },
  });

  const featureFlags = useMemo(() => {
    const flagMap = new Map<string, boolean>();
    if (data?.activeFeatureFlag) {
      for (const flag of data.activeFeatureFlag) {
        flagMap.set(flag.key, flag.enabled);
      }
    }
    return flagMap;
  }, [data?.activeFeatureFlag]);

  // Overwrite `userProfile.account` with `activeAccount` when present so every
  // consumer (navbar, feature gates, role-based rendering) reflects the
  // currently-selected context without needing to change.
  const userProfile = useMemo<UserProfile | undefined>(() => {
    if (!data?.userProfile) {
      return undefined;
    }
    if (!data.activeAccount) {
      return data.userProfile;
    }
    return { ...data.userProfile, account: data.activeAccount };
  }, [data?.userProfile, data?.activeAccount]);

  const previousUserProfile = useMemo<UserProfile | undefined>(() => {
    if (!previousData?.userProfile) {
      return undefined;
    }
    if (!previousData.activeAccount) {
      return previousData.userProfile;
    }
    return { ...previousData.userProfile, account: previousData.activeAccount };
  }, [previousData?.userProfile, previousData?.activeAccount]);

  return {
    userProfile,
    previousUserProfile,
    featureFlags,
    ...rest,
  };
}

export type UseUserProfileOptions = Omit<QueryHookOptions<GetUserProfileData, GetUserProfileVars>, "variables">;

export interface UseUserProfileResult {
  userProfile?: UserProfile;
  previousUserProfile?: UserProfile;
  featureFlags: Map<string, boolean>;
  loading: boolean;
  error?: ApolloError;
  networkStatus: NetworkStatus;
  called: boolean;
  refetch: QueryResult<GetUserProfileData, GetUserProfileVars>["refetch"];
  client: ApolloClient<unknown>;
}

export default useUserProfile;
